import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DeclinedReason, DeclinedReasonService, ListDeclinedReasonsFilters } from '@vendasta/sales-orders';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { PARTNER_ID_INJECTION_TOKEN } from '../../core/constants';
import { getImageSrc } from '../../core/images';
import {
  Action,
  DeclinedReasonDialogComponent,
  DeclinedReasonDialogData,
} from './dialog/declined-reason-dialog.component';

@Component({
  styleUrls: ['sales-orders-decline-reasons.component.scss'],
  templateUrl: './sales-orders-decline-reasons.component.html',
  standalone: false,
})
export class OrderDeclineReasonsConfigComponent implements OnInit {
  private readonly loading$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  readonly loading$: Observable<boolean> = this.loading$$.asObservable();
  private readonly error$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly error$: Observable<boolean> = this.error$$.asObservable();
  declineReasons$: Observable<DeclinedReason[]>;
  private readonly reload$$: Subject<null> = new Subject<null>();

  readonly action = Action;
  readonly getImageSrc = getImageSrc;

  constructor(
    @Inject(PARTNER_ID_INJECTION_TOKEN) private readonly partnerId$: Observable<string>,
    private readonly declinedReasonsService: DeclinedReasonService,
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
    readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    const reload$ = this.reload$$.pipe(startWith(null));
    this.declineReasons$ = combineLatest([this.partnerId$, reload$]).pipe(
      tap(() => this.loading$$.next(true)),
      switchMap(([partnerId, _]) =>
        this.declinedReasonsService.list(partnerId, { includeArchived: false } as ListDeclinedReasonsFilters).pipe(
          catchError(() => {
            this.snackbarService.openErrorSnack(
              this.translateService.instant('CUSTOMIZE_SALES_ORDERS_DECLINE_REASONS.LIST_ERROR'),
            );
            return of([]);
          }),
        ),
      ),
      tap(() => this.loading$$.next(false)),
      shareReplay(1),
    );
  }

  openActionDialog(action: Action, reason?: DeclinedReason): void {
    this.partnerId$
      .pipe(
        take(1),
        switchMap((partnerId) => {
          const dialogData: DeclinedReasonDialogData = {
            action,
            partnerId,
            reason,
          };
          return this.dialog
            .open(DeclinedReasonDialogComponent, {
              width: '600px',
              data: dialogData,
            } as MatDialogConfig)
            .afterClosed();
        }),
      )
      .subscribe((isSuccess) => {
        if (isSuccess) {
          this.reload$$.next(null);
        }
      });
  }
}
